// src/sim/catch/escort.ts — P3 leash-with-wall-clamp escort motion (GAME-04, D-04).
//
// Invariants:
//   - Pure: returns updates Record; never mutates inputs (Pitfall 1).
//   - Uses clampMotionAgainstWalls (P1) so walls block the leash.
//   - Emits no events (catch.ts owns 'catch'; drop-off.ts owns 'hider-parked';
//     free-all.ts owns 'hider-released').
//
// WARNING-11 contract: Escort-driven position updates do NOT emit entity-moved.
// GameScene reads currState.entities each frame for sprite sync — the sprite
// follows the hunter visually without a sim event. P6 footstep audio will need
// an escort-step heuristic (e.g. per-tick velocity > threshold while
// status='caught') rather than subscribing to entity-moved. Document that
// contract here so the P6 audio planner knows not to rely on entity-moved for
// escorted hiders.
//
// Source: .planning/phases/03-core-sim-primitives/03-CONTEXT.md D-04.

import type { Entity, WorldState, Vec2 } from '../../shared/types/world';
import type { MapData } from '../../shared/types/map';
import {
  P1_CHARACTER_RADIUS,
  P1_DEFAULT_BASE_SPEED,
  clampMotionAgainstWalls,
} from '../movement';
import { P3_ESCORT_OFFSET_TILES, P3_ESCORT_SPEED_MULTIPLIER } from './constants';

/**
 * Walk every caught hider one tick toward a target placed 1 tile behind their
 * escorting hunter's facing. Returns partial entity updates keyed by hider id.
 * Hunters are not modified by this stage.
 */
export function applyEscortLeash(
  world: WorldState,
  mapData: MapData | null | undefined,
  dt: number,
): Record<string, Entity> {
  const updates: Record<string, Entity> = {};
  if (!mapData) return updates; // legacy mapData-less fixtures: no-op
  const offsetPx = mapData.tileWidth * P3_ESCORT_OFFSET_TILES;

  for (const hider of Object.values(world.entities)) {
    if (hider.status !== 'caught' || hider.escortedBy === null) continue;
    const hunter = world.entities[hider.escortedBy];
    if (!hunter || hunter.status !== 'escorting') continue;

    // Target 1 tile behind hunter facing (D-04). Mirror of the camera-lookahead
    // sign convention used in GameScene: cos/sin point forward; negate for
    // "behind".
    const target: Vec2 = {
      x: hunter.position.x - Math.cos(hunter.facing) * offsetPx,
      y: hunter.position.y - Math.sin(hunter.facing) * offsetPx,
    };
    const dx = target.x - hider.position.x;
    const dy = target.y - hider.position.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist < 0.5) {
      // Already at target — only snap visual attachment.
      updates[hider.id] = { ...hider, facing: hunter.facing };
      continue;
    }

    const speed = P1_DEFAULT_BASE_SPEED * P3_ESCORT_SPEED_MULTIPLIER;
    const step = Math.min(dist, speed * dt);
    const intended: Vec2 = {
      x: hider.position.x + (dx / dist) * step,
      y: hider.position.y + (dy / dist) * step,
    };
    const next = clampMotionAgainstWalls(
      hider.position,
      intended,
      P1_CHARACTER_RADIUS,
      mapData,
    );
    updates[hider.id] = { ...hider, position: next, facing: hunter.facing };
  }
  return updates;
}
